import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { logError } from '../utils/logger';
import { createJsonRpcToolCallRequest } from './mcpBridgeUtils';
import { findInvalidToolSchemas } from './validateMcpToolSchemas';

// Tool schema as sent by the MCP host (matches mockMcpTools.ts on the shell-ui side)
export type McpTool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: {
    readOnlyHint?: boolean;
  };
};

// JSON-RPC 2.0 tools/call request (as defined by modelcontextprotocol)
export type JsonRpcToolCallRequest = {
  jsonrpc: '2.0';
  id: string | number;
  method: 'tools/call';
  params: {
    name: string;
    arguments: Record<string, unknown>;
  };
};

export type ToolCallParams = JsonRpcToolCallRequest['params'];

// The MCP CallToolResult surfaced by the host (see @mcp-b normalizeToolResponse).
export type McpToolResult = { content?: unknown[]; structuredContent?: unknown; isError?: boolean };

// Host -> app tool events, relayed to subscribers (the chat loop).
export type HostToolEvent =
  | { kind: 'ack'; id: string | number }
  | { kind: 'response'; id: string | number; result?: unknown; error?: unknown };

/**
 * Pure MCP transport.
 *
 * This provider owns ONLY the `postMessage` plumbing: tool discovery (with the
 * "ignore transient empty discovery" guard), the instance-id handshake, sending
 * `tools/call` to the host, and relaying ACK/RESPONSE to subscribers. It knows
 * nothing about chat, mutations, pending-call bookkeeping, timeouts or tool
 * authorization — those live in the chat reducer / `ChatProvider`.
 */
export type McpBridgeContextType = {
  isInIframe: boolean;
  mcpTools: McpTool[];
  mcpInstanceId: string | null;
  /**
   * The deployment version the host reports through `getPlatformIdentity`, or null when it
   * offers no such tool. Refreshed whenever the model calls that tool, e.g. after an upgrade.
   */
  platformVersion: string | null;
  /** Send a tools/call to the host (raw postMessage, no bookkeeping). */
  dispatchToolCall: (request: JsonRpcToolCallRequest) => void;
  /**
   * postMessage to the embedding host, targeted at the origin learned from the
   * handshake. No-ops until that origin is known, so nothing is ever broadcast
   * with a wildcard targetOrigin.
   */
  postToHost: (message: unknown) => boolean;
  /** Subscribe to host ACK/RESPONSE messages. Returns an unsubscribe fn. */
  subscribe: (handler: (event: HostToolEvent) => void) => () => void;
  /**
   * Promise-based tools/call for SIDE-BAND calls (background-task polling). Its
   * response is resolved here and NEVER broadcast to `subscribe`, so a poll never
   * enters the chat loop as an LLM tool result.
   */
  callTool: (params: ToolCallParams) => Promise<McpToolResult>;
};

export const McpBridgeContext = createContext<McpBridgeContextType>({
  isInIframe: false,
  mcpTools: [],
  mcpInstanceId: null,
  platformVersion: null,
  dispatchToolCall: () => {},
  postToHost: () => false,
  subscribe: () => () => {},
  callTool: () => Promise.reject(new Error('MCP bridge not ready')),
});

export const useMcpBridge = () => useContext(McpBridgeContext);

const GET_INSTANCE_ID_TOOL = 'getInstanceId';
const GET_INSTANCE_ID_CALL_ID = 'mcp-internal-get-instance-id';
// Offered by ARTESCA only: RING and ADI hosts keep to getInstanceId.
const GET_PLATFORM_IDENTITY_TOOL = 'getPlatformIdentity';
const GET_PLATFORM_IDENTITY_CALL_ID = 'mcp-internal-get-platform-identity';

const readPlatformVersion = (result: unknown): string | null => {
  const content = (result as McpToolResult | undefined)?.structuredContent as { version?: unknown } | undefined;
  return typeof content?.version === 'string' && content.version ? content.version : null;
};

export const McpBridgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isInIframe = window.self !== window.top;
  const [mcpTools, setMcpTools] = useState<McpTool[]>([]);
  const [mcpToolOrigin, setMcpToolOrigin] = useState<string>();
  const [mcpInstanceId, setMcpInstanceId] = useState<string | null>(null);
  const [platformVersion, setPlatformVersion] = useState<string | null>(null);

  // Subscribers to host tool events (ACK/RESPONSE). The chat loop registers one.
  const subscribersRef = useRef(new Set<(event: HostToolEvent) => void>());
  const subscribe = useCallback((handler: (event: HostToolEvent) => void) => {
    subscribersRef.current.add(handler);
    return () => {
      subscribersRef.current.delete(handler);
    };
  }, []);
  const emit = useCallback((event: HostToolEvent) => {
    for (const handler of subscribersRef.current) handler(event);
  }, []);

  // Live mirror of the host origin so the callbacks below (registered once) can
  // target the correct window without re-binding the listener.
  const mcpToolOriginRef = useRef<string | undefined>(undefined);
  mcpToolOriginRef.current = mcpToolOrigin;

  const postToHost = useCallback((message: unknown) => {
    const origin = mcpToolOriginRef.current;
    if (!origin) {
      console.warn('[McpBridge] host origin unknown (no handshake yet) — message dropped:', message);
      return false;
    }
    window.parent.postMessage(message, origin);
    return true;
  }, []);

  // Tool names already reported as carrying an invalid inputSchema, so a repeated
  // discovery broadcast (~every 3s) never floods console.error / Sentry.
  const reportedInvalidSchemasRef = useRef(new Set<string>());

  // Ids of the model's own getPlatformIdentity calls, whose responses also refresh the version.
  const platformIdentityCallIdsRef = useRef(new Set<string | number>());

  const dispatchToolCall = useCallback(
    (request: JsonRpcToolCallRequest) => {
      if (request.params.name === GET_PLATFORM_IDENTITY_TOOL) platformIdentityCallIdsRef.current.add(request.id);
      if (postToHost({ type: 'MCP_CALL', payload: request })) {
        console.debug('[McpBridge] MCP_CALL sent:', request.params.name, 'id:', request.id);
      }
    },
    [postToHost],
  );

  // Side-band tools/call: pending promises keyed by request id, resolved by the
  // MCP_RESPONSE handler below (which intercepts these ids BEFORE broadcasting to the
  // chat loop). Used for background-task polling — the `call-*` id namespace never
  // collides with the chat loop's tool_use ids.
  const pending = useRef(new Map<string, { resolve: (r: McpToolResult) => void; reject: (e: unknown) => void }>());
  const seq = useRef(0);
  const callTool = useCallback(
    (params: ToolCallParams): Promise<McpToolResult> =>
      new Promise((resolve, reject) => {
        const id = `call-${++seq.current}`;
        pending.current.set(id, { resolve, reject });
        // Go through postToHost like dispatchToolCall does: with no handshake there is no known
        // host origin, and falling back to a wildcard targetOrigin would broadcast the call to
        // any window listening. Refuse instead.
        if (!postToHost({ type: 'MCP_CALL', payload: { jsonrpc: '2.0', id, method: 'tools/call', params } })) {
          pending.current.delete(id);
          reject(new Error('MCP bridge not ready: the host origin is unknown'));
          return;
        }
        // Safety net: never leak a pending promise if a response is lost.
        setTimeout(() => {
          if (pending.current.delete(id)) reject(new Error('tool call timed out'));
        }, 60_000);
      }),
    [postToHost],
  );

  // Request the host instance id once we know the host origin.
  useEffect(() => {
    if (!mcpToolOrigin) return;
    window.parent.postMessage(
      createJsonRpcToolCallRequest({ name: GET_INSTANCE_ID_TOOL, arguments: {} }, GET_INSTANCE_ID_CALL_ID),
      mcpToolOrigin,
    );
  }, [mcpToolOrigin]);

  const offersPlatformIdentity = mcpTools.some((tool) => tool.name === GET_PLATFORM_IDENTITY_TOOL);
  useEffect(() => {
    if (!mcpToolOrigin || !offersPlatformIdentity) return;
    window.parent.postMessage(
      createJsonRpcToolCallRequest({ name: GET_PLATFORM_IDENTITY_TOOL, arguments: {} }, GET_PLATFORM_IDENTITY_CALL_ID),
      mcpToolOrigin,
    );
  }, [mcpToolOrigin, offersPlatformIdentity]);

  // Listen for messages from the parent (shell-ui).
  useEffect(() => {
    // A failed read says nothing about the version, so it never erases a known one.
    const keepReadVersion = (result: unknown) => {
      const version = readPlatformVersion(result);
      if (version) setPlatformVersion(version);
    };

    const handler = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      setMcpToolOrigin(event.origin);

      const { type, id, result, error, tools } = event.data ?? {};

      if (id === GET_INSTANCE_ID_CALL_ID) {
        if (type === 'MCP_RESPONSE') {
          setMcpInstanceId(result?.structuredContent?.instanceId ?? null);
        }
        return;
      }

      if (id === GET_PLATFORM_IDENTITY_CALL_ID) {
        if (type === 'MCP_RESPONSE') keepReadVersion(result);
        return;
      }
      if (type === 'MCP_RESPONSE' && platformIdentityCallIdsRef.current.delete(id)) {
        keepReadVersion(result);
      }

      // Side-band poll (callTool): resolve/reject its promise and DON'T broadcast it
      // to the chat loop. The matching ACK is likewise swallowed.
      if (typeof id === 'string' && pending.current.has(id)) {
        if (type === 'MCP_ACK') return;
        if (type === 'MCP_RESPONSE') {
          const p = pending.current.get(id);
          pending.current.delete(id);
          if (p) (error ? p.reject(error) : p.resolve(result as McpToolResult));
          return;
        }
      }

      switch (type) {
        case 'MCP_DISCOVERY':
          // The parent broadcasts MCP_DISCOVERY on a periodic interval (~3s) and
          // the list briefly goes empty during token rotation / host
          // re-registration. Treat empty discoveries as transient once we have a
          // cached list, so a tool-result turn never ships `mcp_tools: []`.
          if (Array.isArray(tools) && tools.length > 0) {
            const invalid = findInvalidToolSchemas(tools);
            for (const { name, errors } of invalid) {
              if (reportedInvalidSchemasRef.current.has(name)) continue;
              reportedInvalidSchemasRef.current.add(name);
              logError(
                new Error(`MCP tool "${name}" advertised an invalid inputSchema`),
                'Dropping MCP tool with invalid inputSchema received from WebMCP host',
                { tool: name, errors },
              );
            }
            // Drop tools whose schema the LLM providers would reject so they are
            // never forwarded to the backend and never acknowledged by the model.
            const invalidNames = new Set(invalid.map((i) => i.name));
            setMcpTools(invalidNames.size > 0 ? tools.filter((t) => !invalidNames.has(t.name)) : tools);
          } else {
            setMcpTools((prev) => (prev.length > 0 ? prev : []));
          }
          break;

        case 'MCP_ACK':
          emit({ kind: 'ack', id });
          break;

        case 'MCP_RESPONSE':
          emit({ kind: 'response', id, result, error });
          break;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [emit]);

  const value = useMemo(
    () => ({
      isInIframe,
      mcpTools,
      mcpInstanceId,
      platformVersion,
      dispatchToolCall,
      postToHost,
      subscribe,
      callTool,
    }),
    [isInIframe, mcpTools, mcpInstanceId, platformVersion, dispatchToolCall, postToHost, subscribe, callTool],
  );

  return <McpBridgeContext.Provider value={value}>{children}</McpBridgeContext.Provider>;
};
